import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "px-6 md:px-8 pt-6 pb-20" }
const _hoisted_2 = { class: "max-w-[1040px]" }
const _hoisted_3 = { class: "flex justify-end mb-4" }
const _hoisted_4 = { class: "w-44" }
const _hoisted_5 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" }
const _hoisted_6 = { class: "text-2xl font-semibold text-gray-900" }
const _hoisted_7 = { class: "text-sm text-gray-500" }
const _hoisted_8 = { class: "text-2xl font-semibold text-gray-900" }
const _hoisted_9 = { class: "text-sm font-medium text-gray-700 mb-3" }
const _hoisted_10 = { class: "relative h-72" }
const _hoisted_11 = {
  key: 0,
  class: "absolute inset-0 flex items-center justify-center text-sm text-gray-500 pointer-events-none"
}
const _hoisted_12 = { class: "overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm" }
const _hoisted_13 = { class: "w-full text-left text-sm" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "py-3 pl-4 pr-4 font-medium text-gray-900" }
const _hoisted_16 = {
  key: 0,
  class: "block text-xs text-gray-500 font-normal"
}
const _hoisted_17 = { class: "py-3 pr-4 text-gray-700" }
const _hoisted_18 = { class: "py-3 pr-4 text-gray-700" }
const _hoisted_19 = { class: "py-3 pr-4 text-gray-700" }
const _hoisted_20 = { class: "py-3 pr-4" }
const _hoisted_21 = {
  key: 0,
  class: "mt-4 flex items-center justify-between"
}
const _hoisted_22 = { class: "text-sm text-gray-500" }

import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import BarChart from '@/components/BarChart.vue'
import EmptyState from '@/components/neue/EmptyState.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import NeueWell from '@/components/neue/NeueWell.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import SecondaryNav from '@/components/SecondaryNav.vue'
import SecondaryTop from '@/components/SecondaryTop.vue'
import { rangeOptions } from '@/composables/useAgentOverview'
import { resellerName, resellerPath } from '@/config/nav/resellers'
import { dateObjectToServerDay } from '@/helpers/dates'
import api from '@/store/api'
import { mapTypeIncludingInactive } from '@/store/mappers'
import AddResellerModal from '@/view/resellers/AddResellerModal.vue'

// Single series color, validated for contrast against a white/near-white chart
// surface (dataviz palette slot 1). ResellerDetail.vue uses the same one.
const SERIES_CART_VALUE = '#2a78d6'
const GRIDLINE = '#e5e7eb'
const AXIS_INK = '#6b7280'

const pageSize = 25

export default {
  __name: 'ResellerList',
  setup(__props) {

const router = useRouter()

const loading = ref(true)
const addOpen = ref(false)
const reportRows = ref([])

const rangeKey = ref(30)
const rangeLabel = computed(() => `last ${rangeKey.value} days`)

// BOT-1610: the table is paged from the server. The active-reseller tile and
// the zero-sales chart need every reseller, so they read the store, which the
// section's nav prefetch (resellers?included=none) fills with all of them.
const resellers = ref([])
const resellersPage = ref(0)
const resellersTotal = ref(0)
const pageCount = computed(() => Math.max(1, Math.ceil(resellersTotal.value / pageSize)))
const activeResellers = computed(() => mapTypeIncludingInactive('reseller').filter(r => r.attributes.is_active))

const formatCurrency = value =>
  `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const totalCartValue = computed(() =>
  reportRows.value.reduce((sum, r) => sum + Number(r.attributes.owned_cart_value || 0), 0)
)

const hasSales = computed(() => reportRows.value.length > 0)

// Keyed by reseller_id (always present, unique) rather than reseller_name:
// the report SQL COALESCEs a blank name to 'Unknown', so multiple unnamed
// resellers would otherwise collapse into a single bar.
const salesByReseller = () => {
  const byReseller = new Map()
  reportRows.value.forEach(r => {
    const id = r.attributes.reseller_id
    const name = r.attributes.reseller_name || `Reseller ${id}`
    const entry = byReseller.get(id) || { name, cartValue: 0 }
    entry.cartValue += Number(r.attributes.owned_cart_value || 0)
    byReseller.set(id, entry)
  })
  const entries = [...byReseller.values()]
  return { labels: entries.map(e => e.name), data: entries.map(e => e.cartValue) }
}

// Empty state: one zero bar per active reseller so the chart keeps its shape.
const zeroByReseller = () => ({
  labels: activeResellers.value.map(r => resellerName(r)),
  data: activeResellers.value.map(() => 0)
})

const chartData = computed(() => {
  const { labels, data } = hasSales.value ? salesByReseller() : zeroByReseller()
  return {
    labels,
    datasets: [
      {
        label: 'Cart value',
        backgroundColor: SERIES_CART_VALUE,
        data,
        borderRadius: 4,
        categoryPercentage: 0.6,
        barPercentage: 0.9
      }
    ]
  }
})

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    title: { display: false },
    tooltip: {
      enabled: hasSales.value,
      padding: 10,
      cornerRadius: 6,
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      backgroundColor: '#111827',
      callbacks: {
        label: ctx =>
          `${ctx.dataset.label}: $${Number(ctx.parsed.y || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
    }
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: { color: AXIS_INK }
    },
    y: {
      beginAtZero: true,
      // All-zero data would otherwise draw a $0 to $1 axis.
      ...(hasSales.value ? {} : { suggestedMax: 100 }),
      grid: { color: GRIDLINE },
      border: { display: false },
      ticks: {
        color: AXIS_INK,
        callback: value => `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
      }
    }
  }
}))

const fetchResellers = async () => {
  const params = new URLSearchParams({ limit: pageSize, page: resellersPage.value })
  const res = await api.call('GET', `resellers?${params.toString()}`)
  resellers.value = res?.data?.data ?? []
  resellersTotal.value = res?.data?.total ?? resellers.value.length
}

const fetchReport = async () => {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - rangeKey.value)
  const params = new URLSearchParams({
    beginning_date: dateObjectToServerDay(start),
    ending_date: dateObjectToServerDay(end)
  })
  const res = await api.call('GET', `data_downloads/reports/reseller_revenue_report?${params.toString()}`)
  reportRows.value = res?.data?.data ?? []
}

const prevPage = () => {
  if (resellersPage.value === 0) return
  resellersPage.value--
  return fetchResellers()
}

const nextPage = () => {
  if (resellersPage.value >= pageCount.value - 1) return
  resellersPage.value++
  return fetchResellers()
}

const onCreated = () => {
  resellersPage.value = 0
  return fetchResellers()
}

watch(rangeKey, () => fetchReport())

onMounted(async () => {
  try {
    await Promise.all([fetchResellers(), fetchReport()])
  } finally {
    loading.value = false
  }
})

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(SecondaryNav, null, {
    top: _withCtx(() => [
      _createVNode(SecondaryTop, { text: "Resellers" }, {
        right: _withCtx(() => [
          _createVNode(NeueButton, {
            "data-test": "add-reseller",
            onClick: _cache[0] || (_cache[0] = $event => (addOpen.value = true))
          }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode("Add reseller", -1)
            ]))]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    main: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(NeueLoading, { loading: loading.value }, {
            default: _withCtx(() => [
              (!resellersTotal.value)
                ? (_openBlock(), _createBlock(EmptyState, {
                    key: 0,
                    plural: "resellers",
                    description: "Add a reseller and they can create customers and order on their behalf."
                  }))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createElementVNode("div", _hoisted_3, [
                      _createElementVNode("div", _hoisted_4, [
                        _createVNode(SelectInput, {
                          modelValue: rangeKey.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((rangeKey).value = $event)),
                          slim: "",
                          options: _unref(rangeOptions).map(opt => ({ name: opt.label, value: opt.value }))
                        }, null, 8, ["modelValue", "options"])
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_5, [
                      _createVNode(NeueWell, null, {
                        default: _withCtx(() => [
                          _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-sm text-gray-500" }, "Active resellers", -1)),
                          _createElementVNode("p", _hoisted_6, _toDisplayString(activeResellers.value.length), 1)
                        ]),
                        _: 1
                      }),
                      _createVNode(NeueWell, null, {
                        default: _withCtx(() => [
                          _createElementVNode("p", _hoisted_7, "Cart value (" + _toDisplayString(rangeLabel.value) + ")", 1),
                          _createElementVNode("p", _hoisted_8, _toDisplayString(formatCurrency(totalCartValue.value)), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _createVNode(NeueWell, { class: "mb-6" }, {
                      default: _withCtx(() => [
                        _createElementVNode("p", _hoisted_9, "Cart value by reseller (" + _toDisplayString(rangeLabel.value) + ")", 1),
                        _createElementVNode("div", _hoisted_10, [
                          _createVNode(BarChart, {
                            "chart-data": chartData.value,
                            "chart-options": chartOptions.value
                          }, null, 8, ["chart-data", "chart-options"]),
                          (!hasSales.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_11, " No sales in this range "))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _: 1
                    }),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("table", _hoisted_13, [
                        _cache[5] || (_cache[5] = _createElementVNode("thead", null, [
                          _createElementVNode("tr", { class: "border-b border-gray-200 text-gray-500" }, [
                            _createElementVNode("th", { class: "py-2 pl-4 pr-4 font-medium" }, "Reseller"),
                            _createElementVNode("th", { class: "py-2 pr-4 font-medium" }, "Customers"),
                            _createElementVNode("th", { class: "py-2 pr-4 font-medium" }, "Orders placed"),
                            _createElementVNode("th", { class: "py-2 pr-4 font-medium" }, "Cart value"),
                            _createElementVNode("th", { class: "py-2 pr-4 font-medium" }, "Status")
                          ])
                        ], -1)),
                        _createElementVNode("tbody", null, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resellers.value, (row) => {
                            return (_openBlock(), _createElementBlock("tr", {
                              key: row.id,
                              class: "border-b border-gray-100 hover:bg-gray-50 cursor-pointer",
                              onClick: $event => (_unref(router).push(_unref(resellerPath)(row.id)))
                            }, [
                              _createElementVNode("td", _hoisted_15, [
                                _createTextVNode(_toDisplayString(_unref(resellerName)(row)) + " ", 1),
                                (row.attributes.phone_number)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(row.attributes.phone_number), 1))
                                  : _createCommentVNode("", true)
                              ]),
                              _createElementVNode("td", _hoisted_17, _toDisplayString(row.attributes.customers_count), 1),
                              _createElementVNode("td", _hoisted_18, _toDisplayString(row.attributes.orders_count), 1),
                              _createElementVNode("td", _hoisted_19, _toDisplayString(formatCurrency(row.attributes.owned_cart_value)), 1),
                              _createElementVNode("td", _hoisted_20, [
                                _createElementVNode("span", {
                                  class: _normalizeClass([
                            row.attributes.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          , "px-2 py-0.5 rounded-full text-xs font-medium"])
                                }, _toDisplayString(row.attributes.is_active ? 'Active' : 'Inactive'), 3)
                              ])
                            ], 8, _hoisted_14))
                          }), 128))
                        ])
                      ])
                    ]),
                    (pageCount.value > 1)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                          _createVNode(NeueButton, {
                            type: "secondary",
                            size: "sm",
                            disabled: resellersPage.value === 0,
                            "data-test": "prev-page",
                            onClick: prevPage
                          }, {
                            default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                              _createTextVNode(" Previous ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["disabled"]),
                          _createElementVNode("span", _hoisted_22, "Page " + _toDisplayString(resellersPage.value + 1) + " of " + _toDisplayString(pageCount.value), 1),
                          _createVNode(NeueButton, {
                            type: "secondary",
                            size: "sm",
                            disabled: resellersPage.value >= pageCount.value - 1,
                            "data-test": "next-page",
                            onClick: nextPage
                          }, {
                            default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                              _createTextVNode(" Next ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["disabled"])
                        ]))
                      : _createCommentVNode("", true)
                  ], 64))
            ]),
            _: 1
          }, 8, ["loading"])
        ]),
        _createVNode(AddResellerModal, {
          open: addOpen.value,
          onClose: _cache[2] || (_cache[2] = $event => (addOpen.value = false)),
          onCreated: onCreated
        }, null, 8, ["open"])
      ])
    ]),
    _: 1
  }))
}
}

}